"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface TocItem {
  id: string;
  title: string;
}

interface Props {
  items: TocItem[];
}

export function PolicyToc({ items }: Props) {
  const [activeId, setActiveId] = useState(items[0]?.id || "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0.1 },
    );

    items.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [items]);

  function scrollTo(id: string) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveId(id);
    }
  }

  return (
    <>
      {/* PC: 좌측 사이드 TOC */}
      <nav className="sticky top-20 hidden max-h-[calc(100vh-6rem)] w-56 shrink-0 overflow-y-auto border-r border-gray-100 pr-4 lg:block">
        <div className="mb-3 text-[10px] font-bold tracking-wider text-gray-400 uppercase">
          목차
        </div>
        <ul className="space-y-0.5">
          {items.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => scrollTo(item.id)}
                className={cn(
                  "w-full rounded-md px-2 py-1.5 text-left text-xs leading-tight transition-colors",
                  activeId === item.id
                    ? "bg-rose-50 font-semibold text-violet-600"
                    : "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
                )}
              >
                {item.title}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* 모바일: 상단 드롭다운 */}
      <div className="sticky top-14 z-40 border-b border-gray-100 bg-white/95 px-5 py-2 backdrop-blur-md lg:hidden">
        <select
          value={activeId}
          onChange={(e) => scrollTo(e.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
        >
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.title}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}
